import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";

import { ADMIN_BASE } from "./base";
import { useSession } from "../composables/useSession";

declare module "vue-router" {
  interface RouteMeta {
    /** Reachable without a session. Only the sign-in page is. */
    signedOut?: boolean;
    /** What the navigation bar calls this page. */
    title?: string;
  }
}

/**
 * German paths, because they end up in the address bar of someone who does not
 * read English. The views are loaded lazily so the sign-in page does not carry
 * the whole admin with it.
 */
const routes: RouteRecordRaw[] = [
  /**
   * The front door, and the one place that decides where someone lands who
   * has not asked for a particular page. The start page rather than the
   * performances: it is what a visitor of the website sees first, so it is
   * what is worth a look first - and it is what the navigation now leads
   * with.
   *
   * Everything else that has nobody to send anywhere in particular sends her
   * here instead of naming a page of its own: the sign-in below, and
   * client/src/views/LoginView.vue after a successful one. Changing this line
   * moves all three.
   */
  { path: "/", redirect: { name: "startseite" } },
  {
    path: "/anmelden",
    name: "anmelden",
    component: () => import("../views/LoginView.vue"),
    meta: { signedOut: true, title: "Anmelden" },
  },
  {
    path: "/auftritte",
    name: "auftritte",
    component: () => import("../views/ShowsView.vue"),
    meta: { title: "Auftritte" },
  },
  {
    path: "/bilder",
    name: "bilder",
    component: () => import("../views/PicturesView.vue"),
    meta: { title: "Bilder" },
  },
  {
    path: "/videos",
    name: "videos",
    component: () => import("../views/VideosView.vue"),
    meta: { title: "Videos" },
  },
  {
    path: "/musik",
    name: "musik",
    component: () => import("../views/MusicView.vue"),
    meta: { title: "Musik" },
  },
  {
    path: "/startseite",
    name: "startseite",
    component: () => import("../views/HighlightView.vue"),
    meta: { title: "Startseite" },
  },
  {
    path: "/passwort",
    name: "passwort",
    component: () => import("../views/PasswordView.vue"),
    meta: { title: "Passwort ändern" },
  },
  {
    path: "/einstellungen",
    name: "einstellungen",
    component: () => import("../views/SettingsView.vue"),
    meta: { title: "Einstellungen" },
  },
  {
    path: "/fehlerprotokoll",
    name: "fehlerprotokoll",
    component: () => import("../views/ErrorLogView.vue"),
    meta: { title: "Fehlerprotokoll" },
  },
  // Anything else inside the admin is a mistyped address, not a server error.
  {
    path: "/:pfad(.*)*",
    name: "nichtGefunden",
    component: () => import("../views/NotFoundView.vue"),
    meta: { signedOut: true, title: "Seite nicht gefunden" },
  },
];

export const router = createRouter({
  history: createWebHistory(ADMIN_BASE),
  routes,
});

/**
 * One look at the server before the first page, then a plain check.
 *
 * A session that drops later is not handled here: the shell asks for the
 * password in a dialog instead, so an open form keeps what was typed into it.
 */
router.beforeEach(async (to) => {
  const { start, isSignedIn } = useSession();

  await start();

  if (isSignedIn.value) {
    // Already signed in and asking for the sign-in page: there is nothing to
    // do there, and no page she was on the way to - so, the front door.
    //
    // The bare address rather than the object form vue-router also takes:
    // test/e2e/client-app.e2e-spec.ts reads this file for the routes the
    // server has to answer for, and a second key of that name here would
    // read to it as a route declared twice.
    return to.name === "anmelden" ? "/" : true;
  }

  if (to.meta.signedOut) {
    return true;
  }

  return { name: "anmelden", query: { weiter: to.fullPath } };
});

router.afterEach((to) => {
  const title = to.meta.title;
  document.title = title ? `${title} – Tubetänzig` : "Tubetänzig";
});
